import { useEffect, useMemo, useState } from "react";
import {
  Button,
  MenuItem,
  CircularProgress,
  Grid,
  InputLabel,
  FormControl,
  Box,
  TextField,
  Container,
  Typography,
  Snackbar,
  Select,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getNumber } from "src/utils/cardUtils";
import { get, put } from "src/utils/networkUtils";
import {
  defaultHockeyPackSettings,
  defaultSoccerPackSettings,
  defaultBasketballPackSettings,
  defaultFootballPackSettings,
  defaultRugbyPackSettings,
  defaultAustralianFootballPackSettings,
  defaultCricketPackSettings,
} from "src/utils/packSettings";
import useWithUrlTenant from "src/hooks/useTenant";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";
import PositionFields from "src/components/packs-admin/PositionFields";
import SPORT_POSITIONS from "src/utils/sportPositions";
export const getDefaultPackSettings = (sport) => {
  if (!sport) {
    return defaultHockeyPackSettings;
  }
  const sportLower = sport.toLowerCase();
  if (sportLower === "soccer") return defaultSoccerPackSettings;
  if (sportLower === "basketball") return defaultBasketballPackSettings;
  if (sportLower === "football") return defaultFootballPackSettings;
  if (sportLower === "rugby") return defaultRugbyPackSettings;
  if (sportLower === "australian_football") return defaultAustralianFootballPackSettings;
  if (sportLower === "cricket") return defaultCricketPackSettings;
  return defaultHockeyPackSettings;
};

export default function PackSettingsPage() {
  const { type } = useParams();
  const navigate = useNavigate();

  const [competitions, setCompetitions] = useState(null);
  const [packSettings, setPackSettings] = useState(null);
  const [totalCard, setTotalCard] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorOpen, setErrorOpen] = useState(false);

  const withTenant = useWithUrlTenant();
  const { tenantId, tenantCompetitions } = useTenantCompetitions();

  const selectedCompetition = tenantCompetitions.find(
    (c) => +c.competitionId === +packSettings?.competitionId
  );
  const sport = selectedCompetition?.sport?.toLowerCase() || "hockey";

  const positionFields = useMemo(() => {
    const safeSport =
    SPORT_POSITIONS[sport] ? sport : "hockey";
    return ["Team", ...SPORT_POSITIONS[safeSport], "Random"];
  }, [sport]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setCompetitions(tenantCompetitions);

        const packResp = await get(
          withTenant(`/admin/packs/settings?packType=${type}`)
        );
        if (packResp.settings.length === 0) {
          // Get sport from first competition if available
          const firstCompetitionSport = tenantCompetitions[0]?.sport?.toLowerCase() || "";
          const defaultSettings = getDefaultPackSettings(firstCompetitionSport);
          // Set default competition
          if (tenantCompetitions.length > 0) {
            defaultSettings.competitionId = tenantCompetitions[0].competitionId;
          }
          setPackSettings(defaultSettings);
        } else {
          setPackSettings(packResp.settings);
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [type, withTenant, tenantCompetitions]);

  useEffect(() => {
    if (
      packSettings &&
      tenantCompetitions.length > 0 &&
      !packSettings.competitionId
    ) {
      setPackSettings((prev) => ({
        ...prev,
        competitionId: tenantCompetitions[0].competitionId,
      }));
    }
  }, [packSettings, tenantCompetitions]);

  const sumFields = (obj, fields = []) =>
    fields.reduce((total, key) => total + getNumber(+obj?.[key]), 0);

  useEffect(() => {
    if (!packSettings) return;
    const total = sumFields(packSettings, positionFields);
    setTotalCard(total);
  }, [packSettings, positionFields]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setPackSettings((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleGradePullChange = (value) => {
    setPackSettings((prev) => ({
      ...prev,
      gradePull: value,
    }));
  };

  const handleCompetitionIdChange = (value) => {
    setPackSettings((prev) => ({
      ...prev,
      competitionId: value,
    }));
  };

  const handleSave = () => {
    const positionTotal = sumFields(packSettings, positionFields);
    const ratingTotal =
      getNumber(+packSettings?.AllStar) +
      getNumber(+packSettings?.Base) +
      getNumber(+packSettings?.Gold) +
      getNumber(+packSettings?.Silver);

    if (positionTotal === ratingTotal) {
      put(`/admin/packs/settings`, {
        settings: packSettings,
        packType: type,
        tenantId: tenantId,
      }).then(() => {
        navigate(-1);
      });
    } else {
      setErrorOpen(true);
    }
  };

  if (loading || !packSettings) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    {packSettings !== null && packSettings.competitionId && (
    <Container sx={{ mt: 1 }}>
      <Typography variant="h5" gutterBottom>
        Packs
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Total cards across positions must equal total cards across ratings.
        <br /> Grade pull percentages (if specified) take precedence over number
        of cards for each rating.
      </Typography>

      <Grid container>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Typography>Number of Cards: {totalCard}</Typography>
        </Grid>

        <Grid item xs={3} sx={{ p: 1 }}>
          <TextField
            id="Gold"
            label="Gold"
            fullWidth
            value={packSettings.Gold}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3} sx={{ p: 1 }}>
          <TextField
            id="Silver"
            label="Silver"
            fullWidth
            value={packSettings.Silver}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3} sx={{ p: 1 }}>
          <TextField
            id="Base"
            label="Base"
            fullWidth
            value={packSettings.Base}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3} sx={{ p: 1 }}>
          <TextField
            id="AllStar"
            label="AllStar"
            fullWidth
            value={packSettings.AllStar}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={3} sx={{ p: 1 }}>
          <TextField
            id="Team"
            label="Team"
            fullWidth
            value={packSettings.Team}
            onChange={handleChange}
          />
        </Grid>

        <PositionFields
  positions={SPORT_POSITIONS[sport]}
  packSettings={packSettings}
  handleChange={handleChange}
/>

        <Grid item xs={3} sx={{ p: 1 }}>
          <TextField
            id="Random"
            label="Random"
            fullWidth
            value={packSettings.Random}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={4} sx={{ p: 1 }}>
          <TextField
            id="gradePull"
            label="Grade Pull %"
            fullWidth
            multiline
            rows={6}
            value={packSettings.gradePull}
            onChange={(e) => handleGradePullChange(e.target.value)}
          />
        </Grid>

        <Grid item xs={8} sx={{ p: 1 }}>
          <FormControl sx={{ minWidth: 320 }}>
            <InputLabel>Competition</InputLabel>
            <Select
              value={selectedCompetition?.competitionId || ""}
              label="Competition"
              onChange={(e) => handleCompetitionIdChange(e.target.value)}
            >
              {competitions &&
                competitions.map((competition) => (
                  <MenuItem key={competition.competitionId} value={competition.competitionId}>
                    {competition.competitionName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button variant="outlined" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Box>

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="Total cards across positions must equal total cards across ratings."
      />
    </Container>
    )}
    </>
  );
}
